import { Loader2, Clock3, CheckCircle2, AlertTriangle, FileText } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import type { ResearchResultPayload, ResearchStreamEvent } from '@/types'
import { MarkdownArticle } from '@/components/creative-lab/MarkdownArticle'

interface ResearchProgressModalProps {
  open: boolean
  tools: string[]
  elapsedSeconds: number
  isStreaming: boolean
  events: ResearchStreamEvent[]
  result: ResearchResultPayload | null
  isLoadingResult: boolean
  onClose: () => void
}

const TOOL_DISPLAY_MAP: Record<string, string> = {
  foreplay: 'Ads Research',
  perplexity: 'Web Research',
  deep_research: 'Deep Research',
  document: 'Document Analysis',
}

function toolDisplayName(tool: string): string {
  const normalized = (tool || '').trim()
  if (!normalized) return ''
  return TOOL_DISPLAY_MAP[normalized] || normalized.replace(/_/g, ' ')
}

function formatToolList(tools: string[]): string {
  return tools
    .map((tool) => toolDisplayName(tool))
    .filter((label) => !!label)
    .join(', ')
}

function sanitizeResearchText(text: string): string {
  return text
    .replace(/\bforeplay\b/gi, 'Ads Research')
    .replace(/\bperplexity\b/gi, 'Web Research')
    .replace(/\bdeep_research\b/gi, 'Deep Research')
    .replace(/o3[-_ ]?deep[-_ ]?research/gi, 'deep research model')
    .replace(/\bo3\b/gi, 'deep research model')
}

function formatElapsed(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
}

function eventMessage(event: ResearchStreamEvent): string {
  const payload = event.data
  if (event.event === 'queries_generated') {
    const keywords = Array.isArray(payload.keywords)
      ? payload.keywords.filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
      : []
    const brands = Array.isArray(payload.brands)
      ? payload.brands.filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
      : []
    const queries = Array.isArray(payload.queries)
      ? payload.queries.filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
      : []

    if (keywords.length > 0 || brands.length > 0) {
      const parts: string[] = []
      if (keywords.length > 0) {
        parts.push(`Keywords: ${sanitizeResearchText(keywords.join(', '))}`)
      }
      if (brands.length > 0) {
        parts.push(`Brands: ${sanitizeResearchText(brands.join(', '))}`)
      }
      return parts.join(' | ')
    }
    if (queries.length > 0) {
      return `Queries: ${sanitizeResearchText(queries.join(' | '))}`
    }
  }
  if (typeof payload.message === 'string') {
    return sanitizeResearchText(payload.message)
  }
  if (event.event === 'insight' && typeof payload.summary === 'string') {
    return sanitizeResearchText(payload.summary)
  }
  if (event.event === 'draft_ready') {
    return 'Report draft is ready.'
  }
  if (event.event === 'report_saved') {
    return 'Report was auto-saved.'
  }
  if (event.event === 'report_save_failed') {
    return 'Auto-save failed (report can still be downloaded).'
  }
  if (event.event === 'completed') {
    return 'Research completed.'
  }
  if (event.event === 'failed') {
    return 'Research failed.'
  }
  return event.event
}

function eventLabel(eventName: string): string {
  const map: Record<string, string> = {
    task_started: 'Task Started',
    tool_started: 'Tool Started',
    tool_completed: 'Tool Completed',
    insight: 'Insight',
    draft_ready: 'Draft Ready',
    queries_generated: 'Queries Ready',
    report_saved: 'Report Saved',
    report_save_failed: 'Report Save Failed',
    completed: 'Completed',
    failed: 'Failed',
  }
  return map[eventName] || eventName.replace(/_/g, ' ')
}

function formatEventTime(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

export function ResearchProgressModal(props: ResearchProgressModalProps) {
  const {
    open,
    tools,
    elapsedSeconds,
    isStreaming,
    events,
    result,
    isLoadingResult,
    onClose,
  } = props

  const filteredEvents = events.filter((event) => event.event !== 'heartbeat')
  const insights = filteredEvents.filter((event) => event.event === 'insight').slice(-6)
  const isFailed = filteredEvents.some((event) => event.event === 'failed')
  const showDraft = Boolean(result?.report_draft)

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-4xl w-[96vw] max-h-[90vh] overflow-hidden p-0 gap-0">
        <DialogHeader className="px-6 py-4 border-b border-gray-200">
          <DialogTitle className="flex items-center gap-2">
            {isStreaming ? (
              <Loader2 className="h-5 w-5 animate-spin text-spectrum-emerald" />
            ) : isFailed ? (
              <AlertTriangle className="h-5 w-5 text-red-500" />
            ) : (
              <CheckCircle2 className="h-5 w-5 text-emerald-500" />
            )}
            Research Progress
          </DialogTitle>
          <DialogDescription className="flex items-center gap-4 text-xs">
            <span className="inline-flex items-center gap-1">
              <Clock3 className="h-3.5 w-3.5" />
              Elapsed {formatElapsed(elapsedSeconds)}
            </span>
            <span>Tools: {formatToolList(tools) || '-'}</span>
            {tools.includes('deep_research') && (
              <span className="text-amber-600">Deep Research selected: runtime may increase.</span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-2 h-[70vh]">
          <section className="border-r border-gray-200 overflow-auto p-5 space-y-4">
            <h3 className="text-sm font-semibold text-gray-900">Live Events</h3>
            <div className="space-y-2">
              {filteredEvents.length === 0 && (
                <p className="text-sm text-gray-500">Waiting for first event...</p>
              )}
              {filteredEvents.map((event, index) => (
                <div
                  key={`${event.id ?? index}-${event.event}`}
                  className="rounded-md border p-2.5 bg-gray-50/70"
                >
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs font-semibold text-gray-800">{eventLabel(event.event)}</p>
                    <p className="text-[11px] text-gray-500">{formatEventTime(event.received_at)}</p>
                  </div>
                  {'tool' in event.data && typeof event.data.tool === 'string' ? (
                    <p className="text-[11px] text-emerald-700 mt-1">
                      Tool: {toolDisplayName(event.data.tool)}
                    </p>
                  ) : null}
                  <p className="text-xs text-gray-600 mt-1 leading-relaxed">{eventMessage(event)}</p>
                </div>
              ))}
            </div>

            <h3 className="text-sm font-semibold text-gray-900 pt-2">Key Insights</h3>
            <div className="space-y-2">
              {insights.length === 0 && (
                <p className="text-sm text-gray-500">Insights will appear as tools produce findings.</p>
              )}
              {insights.map((event, index) => (
                <div key={`${event.id ?? index}-insight`} className="rounded-md border p-2.5">
                  <p className="text-xs font-medium text-gray-800">
                    {(event.data.title as string | undefined) || 'Insight'}
                  </p>
                  <p className="text-xs text-gray-600 mt-1">
                    {sanitizeResearchText((event.data.summary as string | undefined) || '-')}
                  </p>
                </div>
              ))}
            </div>
          </section>

          <section className="overflow-auto p-5 space-y-3">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
              <FileText className="h-4 w-4" />
              Report Draft
            </h3>
            {isLoadingResult && (
              <div className="text-sm text-gray-500 flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading report draft...
              </div>
            )}
            {!isLoadingResult && !showDraft && (
              <p className="text-sm text-gray-500">
                Draft not ready yet.
              </p>
            )}
            {showDraft && (
              <>
                <div className="rounded-md border bg-gray-50 px-3 py-2">
                  <p className="text-xs text-gray-600 mb-1">
                    Ideas: <span className="font-medium text-gray-900">{result?.report_draft?.total_ideas ?? 0}</span>
                  </p>
                  <p className="text-xs text-gray-600">
                    Sources:{' '}
                    <span className="font-medium text-gray-900">
                      {result?.sources_used.map((source) => toolDisplayName(source)).join(', ') || '-'}
                    </span>
                  </p>
                </div>
                <div className="bg-white rounded-md border p-3 max-h-[48vh] overflow-auto">
                  <MarkdownArticle markdown={result?.report_draft?.markdown || ''} />
                </div>
              </>
            )}
          </section>
        </div>

        <div className="border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-2">
          <Button onClick={onClose} disabled={isStreaming}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
